import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Radio } from 'lucide-react';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { SignOutButton } from '@/components/auth/SignOutButton';
import {
  adminApi,
  type AdminStreamDatasetStatus,
  type AdminStreamProfileRow,
} from '@/api/adminApi';

const QUERY_KEY = ['admin-stream-subscriptions'] as const;

function datasetBadge(row: AdminStreamDatasetStatus): { label: string; variant: BadgeVariant } {
  const status = (row.status || '').toUpperCase();
  if (status === 'MISSING' || !status) {
    return { label: 'missing', variant: 'warning' };
  }
  if (status === 'ACTIVE') {
    return { label: 'active', variant: 'success' };
  }
  if (status.includes('PENDING')) {
    return { label: 'pending', variant: 'brand' };
  }
  if (status === 'ARCHIVED') {
    return { label: 'archived', variant: 'archived' };
  }
  return { label: status.toLowerCase(), variant: 'neutral' };
}

function subscribeSummary(created: number, reused: number, failed: number, skipped: number) {
  return `Created ${created}, reused ${reused}, failed ${failed}, skipped ${skipped}`;
}

export function AdminStreamSubscriptionsPage() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => adminApi.getStreamSubscriptions(),
  });

  const subscribeMutation = useMutation({
    mutationFn: (profileId?: string) => adminApi.subscribeStreamDatasets(profileId),
    onSuccess: async (res, profileId) => {
      const totals = res.data?.totals;
      showToast(
        totals
          ? subscribeSummary(totals.created, totals.reused, totals.failed, totals.skipped)
          : 'Subscribe requested',
        totals && totals.failed > 0 ? 'error' : 'success'
      );
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      if (profileId) setExpandedId(profileId);
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : 'Subscribe failed', 'error');
    },
  });

  const summary = data?.data;
  const items = summary?.items ?? [];
  const catalog = useMemo(() => {
    const map = new Map((summary?.datasets ?? []).map((spec) => [spec.datasetId, spec.label]));
    return map;
  }, [summary?.datasets]);

  return (
    <div className="min-h-screen bg-page">
      <header className="bg-surface border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <ProductBrand variant="light" showTagline={false} />
            <div className="hidden sm:flex items-center gap-2 border-l border-border pl-4">
              <Radio className="w-5 h-5 text-brand shrink-0" aria-hidden />
              <h1 className="text-headline-md font-semibold text-foreground">Stream subscriptions</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Link to="/admin/report-coverage" className="text-body-sm text-brand hover:underline font-medium">
              Report coverage
            </Link>
            <Link to="/admin/users" className="text-body-sm text-brand hover:underline font-medium">
              Users
            </Link>
            <Link to="/accounts" className="text-body-sm text-foreground-muted hover:text-foreground">
              Accounts
            </Link>
            <SignOutButton />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-foreground-muted mb-1">
              Subscribe every non-DSP Marketing Stream dataset for advertising accounts. Apply the
              SQS queue policy first or new subscriptions stay pending confirmation.
            </p>
            {summary?.totals ? (
              <p className="text-sm text-foreground-subtle">
                {summary.totals.complete} complete / {summary.totals.profiles} profiles ·{' '}
                {summary.totals.datasetCount} datasets
              </p>
            ) : null}
          </div>
          <Button
            type="button"
            onClick={() => subscribeMutation.mutate(undefined)}
            disabled={subscribeMutation.isPending || items.length === 0}
          >
            {subscribeMutation.isPending && !subscribeMutation.variables
              ? 'Subscribing…'
              : 'Subscribe missing (all accounts)'}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : error ? (
          <p className="text-error">Failed to load stream subscriptions.</p>
        ) : items.length === 0 ? (
          <p className="text-foreground-muted">No advertising profiles found.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-surface">
            <table className="min-w-full text-left text-body-sm">
              <thead className="border-b border-border bg-surface-muted text-foreground-muted">
                <tr>
                  <th className="px-4 py-3 font-medium">Account</th>
                  <th className="px-4 py-3 font-medium">Coverage</th>
                  <th className="px-4 py-3 font-medium">Missing</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <ProfileRows
                    key={row.profileId}
                    row={row}
                    catalog={catalog}
                    expanded={expandedId === row.profileId}
                    onToggle={() =>
                      setExpandedId((current) => (current === row.profileId ? null : row.profileId))
                    }
                    onSubscribe={() => subscribeMutation.mutate(row.profileId)}
                    subscribePending={
                      subscribeMutation.isPending && subscribeMutation.variables === row.profileId
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function ProfileRows({
  row,
  catalog,
  expanded,
  onToggle,
  onSubscribe,
  subscribePending,
}: {
  row: AdminStreamProfileRow;
  catalog: Map<string, string>;
  expanded: boolean;
  onToggle: () => void;
  onSubscribe: () => void;
  subscribePending: boolean;
}) {
  const total = row.subscribedCount + row.missingCount;
  return (
    <>
      <tr className="border-b border-border last:border-0">
        <td className="px-4 py-3 align-top">
          <button type="button" onClick={onToggle} className="text-left">
            <div className="font-medium text-foreground">{row.accountName || row.profileId}</div>
            <div className="text-foreground-subtle font-mono text-xs">{row.profileId}</div>
          </button>
        </td>
        <td className="px-4 py-3 align-top">
          {row.subscribedCount}/{total}
        </td>
        <td className="px-4 py-3 align-top text-foreground-muted">
          {row.missingCount === 0
            ? 'None'
            : row.missingDatasetIds.map((id) => catalog.get(id) || id).join(', ')}
        </td>
        <td className="px-4 py-3 align-top text-right">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onSubscribe}
            disabled={subscribePending || row.missingCount === 0}
          >
            {subscribePending ? 'Subscribing…' : 'Subscribe missing'}
          </Button>
        </td>
      </tr>
      {expanded ? (
        <tr className="border-b border-border bg-surface-muted/40">
          <td colSpan={4} className="px-4 py-3">
            <div className="flex flex-wrap gap-2">
              {row.datasets.map((dataset) => {
                const badge = datasetBadge(dataset);
                return (
                  <span key={dataset.datasetId} className="inline-flex items-center gap-1">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    <span className="text-xs text-foreground-muted">
                      {catalog.get(dataset.datasetId) || dataset.datasetId}
                    </span>
                  </span>
                );
              })}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
